/* pos/sagas*/
import {
  select,
  all,
  fork,
  put,
  takeEvery,
  takeLatest,
  spawn,
} from "redux-saga/effects";
import { has, isNil, includes, isEmpty } from "ramda";
import { Pos, PosActionTypes } from "./types";
import { fetch, save, drop } from "../../repo/pos";
import {
  fetchRequest,
  fetchError,
  fetchSuccess,
  saveRequest,
  saveSuccess,
  saveError,
  deleteRequest,
  deleteSuccess,
  deleteError,
} from "./actions";
import { dirty } from "../session/actions";
import { Error, isAuthError } from "../";
import { exportToVSC, flat } from "../../lib/utils";
import { get } from "lodash";
import { MerchantActionTypes } from "../merchant/types";

function* handleFetch(action: ReturnType<typeof fetchRequest>) {
  try {
    const user = yield fetch(action.payload.id, action.payload.params);
    if (user.errors && !isEmpty(user.errors)) {
      if (isAuthError(user.errors)) {
        yield put(dirty());
      }
      yield put(fetchError(JSON.stringify(user.errors)));
    } else {
      yield put(fetchSuccess(user));
    }
  } catch (err) {
    yield put(fetchError(err));
  }
}

function* watchFetchRequest() {
  yield takeEvery(PosActionTypes.FETCH, handleFetch);
}

function* handleSave(action: ReturnType<typeof saveRequest>) {
  try {
    const user = yield save(action.payload.data);
    // we may still have an error
    // if so then we should yield error
    if (user.errors && !isEmpty(user.errors)) {
      if (isAuthError(user.errors)) {
        yield put(dirty());
      }
      yield put(saveError(JSON.stringify(user.errors)));
      action.payload.callback &&
        action.payload.callback(JSON.stringify(user.errors), null);
    } else {
      yield put(saveSuccess(user));
      action.payload.callback && action.payload.callback(null, user);
    }
  } catch (err) {
    yield put(saveError(err));
    action.payload.callback &&
      action.payload.callback("An error occurred while saving POS", null);
  }
}

function* watchSaveRequest() {
  yield takeEvery(PosActionTypes.SAVE_REQUEST, handleSave);
}

function* handleDelete(action: ReturnType<typeof deleteRequest>) {
  try {
    const dropResponse = yield drop(action.payload.id);

    if (dropResponse.errors && !isEmpty(dropResponse.errors)) {
      if (isAuthError(dropResponse.errors)) {
        yield put(dirty());
      }
      yield put(deleteError(JSON.stringify(dropResponse.errors)));
      action.payload.callback &&
        action.payload.callback(JSON.stringify(dropResponse.errors));
    } else {
      yield put(deleteSuccess());
      action.payload.callback && action.payload.callback(null);
      yield put(fetchRequest(null));
    }
  } catch (err) {
    yield put(deleteError(err));
    action.payload.callback && action.payload.callback(JSON.stringify(err));
  }
}

function* watchDeleteRequest() {
  yield takeLatest(PosActionTypes.DELETE_REQUEST, handleDelete);
}

function* handleDownloadRequest(action: ReturnType<typeof fetchRequest>) {
  try {
    const response = yield fetch(action.payload.id, action.payload.params);
    if (response.errors && !isEmpty(response.errors)) {
      if (isAuthError(response.errors)) {
        yield put(dirty());
      }
      yield put(fetchError(JSON.stringify(response.errors)));
    } else {

      // export to csv file
      const header = action.payload.params["header"] ?? [];
      exportToVSC(response.data,header);
    }
  } catch (err) {
    yield put(fetchError(err));
  }
}


function* watchDownloadRequest() {
  yield takeEvery(PosActionTypes.DOWNLOAD_REQUEST, handleDownloadRequest);
}

export default function* posSaga() {
  yield all([
    spawn(watchFetchRequest),
    spawn(watchSaveRequest),
    spawn(watchDeleteRequest),
    spawn(watchDownloadRequest),
  ]);
}
